import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getApiEndpoint } from '../config/api'

const CompleteProfile = () => {
  const [formData, setFormData] = useState({
    company: '',
    phone: '',
  })
  const [phoneConsent, setPhoneConsent] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  useEffect(() => {
    const token = searchParams.get('token')
    if (token) {
      localStorage.setItem('token', token)
    }
  }, [searchParams])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validate phone consent if phone number is provided
    if (formData.phone && !phoneConsent) {
      setError('Please consent to SMS/phone communication if you want to provide a phone number, or leave the phone field empty.')
      return
    }

    setLoading(true)

    const token = localStorage.getItem('token')

    try {
      const response = await fetch(getApiEndpoint('/auth/complete-profile'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (data.success) {
        // Check user status after completing profile
        if (data.user.status === 'pending') {
          navigate('/pending')
        } else {
          navigate('/portal')
        }
      } else {
        setError(data.message || 'Failed to update profile')
      }
    } catch (err) {
      setError('An error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-center text-3xl font-bold text-gray-900">
          Complete Your Profile
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          We need a bit more information to set up your account
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                Company Name *
              </label>
              <input
                id="company"
                name="company"
                type="text"
                required
                value={formData.company}
                onChange={handleChange}
                className="input-field mt-1"
                placeholder="Your Company Inc."
              />
              <p className="mt-1 text-xs text-gray-500">
                This helps us organize your security assessments
              </p>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number (optional)
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="input-field mt-1"
                placeholder="+1 (555) 123-4567"
              />
              <p className="mt-1 text-xs text-gray-500">
                We may contact you for appointment confirmations and security notifications
              </p>

              {formData.phone && (
                <div className="mt-3">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={phoneConsent}
                      onChange={(e) => setPhoneConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      I consent to receive SMS/phone communications from VigilantEdge regarding my security assessments and appointments. Standard message and data rates may apply. You can opt out at any time.
                    </span>
                  </label>
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 text-red-800 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Complete Profile'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CompleteProfile
